// Implements: F-UIX-008, F-UIX-009, F-UIX-007
/**
 * The login form, and only the form.
 *
 * Pure presentation (F-UIX-009): every value and every handler arrives as a
 * prop, there is no server state here, no `fetch`, no navigation and no memory
 * of what happened. The boundaries table makes that mechanical — a file under
 * a module's `components` folder cannot reach a hook, a client or a container
 * — and this file has nothing it would want from them.
 *
 * The second-factor field and the wait notice arrive as SLOTS rather than as
 * booleans. Whether the code is revealed is a decision about the session, and
 * that decision belongs to `LoginContainer`; where the field sits in the
 * visual and tab order is a decision about the form, and that one is here.
 */

import type { ReactElement, ReactNode, RefObject } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field } from "../../../shared/ui/molecules/Field";
import styles from "./LoginForm.module.css";
import recovery from "./Recovery.module.css";

/** The identifiers the labels point at, exported so a test can name them. */
export const EMAIL_FIELD_ID = "login-email";
export const PASSWORD_FIELD_ID = "login-password";

/** The paragraph both fields describe themselves with while there is an error. */
export const FORM_ERROR_ID = "login-form-error";

export type LoginStep = "credentials" | "code";

export interface LoginFormProps {
  readonly email: string;
  readonly onEmailChange: (value: string) => void;
  /**
   * The password field is uncontrolled on purpose: a controlled value is mirrored into the
   * `value` attribute of the markup. Its value reaches the caller through this handler only.
   */
  readonly onPasswordChange: (value: string) => void;
  /** Called on submit, whatever produced it: the button, or Enter in a field. */
  readonly onSubmit: () => void;
  /** A request is in flight: the button says so and cannot be pressed twice. */
  readonly submitting?: boolean | undefined;
  /**
   * The form accepts no input: the attempt bucket is closed, or a request is
   * already on its way. The caller decides which; both look the same here, and
   * in both the typed values stay where they are.
   */
  readonly disabled?: boolean | undefined;
  /** The sentence the container chose for the failure, already translated. */
  readonly errorMessage?: string | undefined;
  /** The revealed second-factor field, or nothing. */
  readonly secondFactor?: ReactNode;
  /** The wait notice of a `429`, or nothing. */
  readonly notice?: ReactNode;
  /**
   * Which step the card shows. On the `code` step the credential fields stay in the form (hidden,
   * with their values: the one request carries all three) and the card asks for the code alone.
   */
  readonly step?: LoginStep | undefined;
  /** "Volver": the way from the code step back to the credentials. */
  readonly onBack?: (() => void) | undefined;
  /** A handle on the email field, for the caller that moves the focus back to it after "Volver". */
  readonly emailRef?: RefObject<HTMLInputElement | null> | undefined;
  /** P2: «¿Olvidaste tu contraseña?», a slot so the form stays router-free; shown next to the password label. */
  readonly forgotPassword?: ReactNode;
}

export function LoginForm({
  email,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  submitting = false,
  disabled = false,
  errorMessage,
  secondFactor,
  notice,
  step = "credentials",
  onBack,
  emailRef,
  forgotPassword,
}: LoginFormProps): ReactElement {
  const { t } = useTranslation();
  const failed = errorMessage !== undefined;
  const codeStep = step === "code";
  // Both credential fields point at the one error paragraph: the form says the
  // credentials are not valid, never which of the two was wrong.
  const errorDescription = failed ? FORM_ERROR_ID : undefined;

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        // The submit stays inside the application: a native navigation is the
        // one path `SameSite=Lax` leaves open, and it is not how this form
        // reaches the API (DD22).
        event.preventDefault();
        // No re-entrancy check here on purpose: implicit submission is blocked
        // by the platform while the default button is disabled, and
        // `submitting` disables it, so the guard would be an unreachable
        // branch. The `inFlight` ref of `useLogin` is what stops a second.
        onSubmit();
      }}
    >
      <div className={styles.brand}>
        <span className={styles.logo}>
          <Icon name="mark" size="sm" />
        </span>
        {t("app.shell.brand")}
      </div>
      <div>
        <h1 className={styles.title} tabIndex={-1}>
          {codeStep ? t("auth.secondFactor.title") : t("auth.login.title")}
        </h1>
        <p className={styles.subtitle}>{codeStep ? t("auth.secondFactor.subtitle") : t("auth.login.subtitle")}</p>
      </div>

      {notice}

      <div className={styles.fields} hidden={codeStep}>
        <Field id={EMAIL_FIELD_ID} label={t("auth.login.emailLabel")} required>
          <Input
            id={EMAIL_FIELD_ID}
            type="email"
            value={email}
            onChange={onEmailChange}
            inputRef={emailRef}
            autoComplete="username"
            invalid={failed}
            describedBy={errorDescription}
            disabled={disabled}
          />
        </Field>

        {/* v3: «¿Olvidaste tu contraseña?» at the end of the label row, where it is looked for when it fails. */}
        <div className={recovery.passwordRow}>
          <Field id={PASSWORD_FIELD_ID} label={t("auth.login.passwordLabel")} required>
            <Input
              id={PASSWORD_FIELD_ID}
              type="password"
              onChange={onPasswordChange}
              autoComplete="current-password"
              invalid={failed}
              describedBy={errorDescription}
              disabled={disabled}
            />
          </Field>
          {forgotPassword}
        </div>
      </div>

      {secondFactor}

      {failed ? (
        <p id={FORM_ERROR_ID} className={styles.error} role="alert">
          <Icon name="alert-circle" size="sm" />
          <span>{errorMessage}</span>
        </p>
      ) : null}

      <Button variant="primary" type="submit" loading={submitting} disabled={disabled}>
        {codeStep ? t("auth.secondFactor.submit") : t("auth.login.submit")}
      </Button>

      {codeStep ? (
        <Button variant="ghost" type="button" onClick={onBack}>
          {t("auth.secondFactor.back")}
        </Button>
      ) : (
        <p className={styles.help}>{t("auth.login.help")}</p>
      )}
    </form>
  );
}
